import type * as THREE from 'three/webgpu';
import { DODGE_CLIP_BASE64, DODGE_CLIP_METADATA } from './dodgeClipData';

export { DODGE_CLIP_METADATA } from './dodgeClipData';

/** 설치 시 한 번 검사하는 전체 트랙 지문. 암호학적 해시 대신 두 독립 32비트 누산기를 쓴다. */
export function dodgeSourceSignature(clip: THREE.AnimationClip): string {
  let a = 0x811c9dc5, b = 5381;
  const scratch = new DataView(new ArrayBuffer(8));
  const byte = (value: number) => {
    a = Math.imul(a ^ value, 0x01000193) >>> 0;
    b = (Math.imul(b, 33) ^ value) >>> 0;
  };
  const uint = (value: number) => {
    for (let i = 0; i < 4; i++) byte((value >>> (i * 8)) & 255);
  };
  const string = (value: string) => {
    uint(value.length);
    for (let i = 0; i < value.length; i++) uint(value.charCodeAt(i));
  };
  const floats = (values: ArrayLike<number>) => {
    uint(values.length);
    for (let i = 0; i < values.length; i++) {
      scratch.setFloat32(0, values[i], true);
      for (let j = 0; j < 4; j++) byte(scratch.getUint8(j));
    }
  };
  string(clip.name);
  scratch.setFloat64(0, clip.duration, true);
  for (let i = 0; i < 8; i++) byte(scratch.getUint8(i));
  uint(clip.blendMode);
  uint(clip.tracks.length);
  for (const track of clip.tracks) {
    string(track.name); string(track.ValueTypeName); uint(track.getInterpolation());
    floats(track.times); floats(track.values);
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

let payload: DataView | undefined;
function readPayload(): DataView {
  if (!payload) {
    const binary = atob(DODGE_CLIP_BASE64);
    if (binary.length !== DODGE_CLIP_METADATA.float32Bytes) throw new Error('회피 데이터 길이 불일치');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    payload = new DataView(bytes.buffer);
  }
  return payload;
}

/** 원본 analyze/analyzeDodge 및 XZ 추출 뒤 호출한다. 실패하면 호출자는 원본을 유지한다.
 * 런타임 피부 검사/재저작 없이 깊은 복제에 승인된 트랙만 설치한다. 게임 시계/구간은 바꾸지 않는다. */
export function createGroundedDodgeClip(source: THREE.AnimationClip): THREE.AnimationClip | null {
  if (dodgeSourceSignature(source) !== DODGE_CLIP_METADATA.sourceSignature) return null;
  const data = readPayload();
  const clone = source.clone();
  const times = new Float32Array(DODGE_CLIP_METADATA.sharedTimeCount);
  for (let i = 0; i < times.length; i++) times[i] = data.getFloat32(i * 4, true);
  for (const entry of DODGE_CLIP_METADATA.tracks) {
    const track = clone.tracks.find(track => track.name === entry.name);
    if (!track || track.ValueTypeName !== entry.type) return null;
    // 클립/인스턴스 사이 가변 배열 공유를 금지한다. 공통 시간은 저장 데이터에서만 공유한다.
    track.setInterpolation(entry.interpolation as THREE.InterpolationModes);
    track.times = times.slice();
    track.values = new Float32Array(entry.valueCount);
    for (let i = 0; i < entry.valueCount; i++) track.values[i] = data.getFloat32(entry.valueOffsetBytes + i * 4, true);
  }
  return clone;
}
